import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { MdEdit, MdColorLens } from "react-icons/md";
import ImageWithFallback from "./components/ImageFallback";
import { fetchActiveCategory, updateCategoryColor } from "../../utils/apis";
import { useSnackbar } from "notistack";
import Switch from '@mui/material/Switch';
import { SketchPicker } from 'react-color';
import debounce from 'lodash.debounce';


const searchContainerStyles = {
  position: "relative",
  width: "100%",
  maxWidth: "420px",
};

const searchInputStyles = {
  width: "100%",
  padding: "12px 44px 12px 44px",
  fontSize: "14px",
  fontWeight: 400,
  color: "var(--ink)",
  backgroundColor: "var(--land-soft)",
  border: "1.5px solid var(--line-strong)",
  borderRadius: "999px",
  outline: "none",
  transition: "all 0.25s ease",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const searchInputFocusStyles = {
  ...searchInputStyles,
  borderColor: "var(--brand)",
  backgroundColor: "#fff",
  boxShadow: "0 0 0 4px var(--brand-ring)",
};

const searchIconStyles = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--ink-3)",
  pointerEvents: "none",
  transition: "color 0.25s ease",
};

const clearBtnStyles = {
  position: "absolute",
  right: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-3)",
  transition: "all 0.2s ease",
};

const SearchIcon = ({ color = "var(--ink-3)" }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ stroke: color }}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HighlightText = ({ text = "", highlight = "" }) => {
  if (!highlight.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "var(--brand-tint-2)",
              color: "var(--brand-hover)",
              padding: "1px 2px",
              borderRadius: "3px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const CategoriesListTable = ({
  categories = [],
  handleChangeStatus,
  onDelete = () => { },
  onEdit = () => { },
  onAdd = () => { },
  onSelectCategory = () => { },
  pagination = { page: 1, limit: 10, totalPages: 1, total: 0 },
  search = "",
  onPageChange = () => {},
  onSearchChange = () => {}
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [color, setColor] = useState('#ffffff');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const colorPickerRef = useRef(null);

  const [isFocused, setIsFocused] = useState(false);

  const handleSwitchChange = async (event, categoryId) => {
    const isActive = event.target.checked;
    await fetchActiveCategory({ categoryId, active: isActive }, enqueueSnackbar);
    handleChangeStatus(categoryId, isActive);
  };

  const handleColorChange = debounce(async (color, categoryId) => {
    setColor(color.hex);
    setSelectedCategory(categoryId);
    await updateCategoryColor(categoryId, color.hex, enqueueSnackbar);
    const index = categories.findIndex((item) => item.shortId === categoryId)
    categories[index].color = color.hex
  }, 300);

  const toggleColorPicker = (categoryId) => {
    setShowColorPicker(showColorPicker === categoryId ? null : categoryId);
  };
  const handleClickOutside = (event) => {
    if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
      setShowColorPicker(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const { totalPages, page: currentPage } = pagination;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = Math.min(4, totalPages - 1);
      }
      if (currentPage >= totalPages - 2) {
        start = Math.max(totalPages - 3, 2);
      }

      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const paginationBtnStyle = (isActive) => ({
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: isActive ? 600 : 400,
    color: isActive ? "#fff" : "var(--ink-2)",
    backgroundColor: isActive ? "var(--brand)" : "transparent",
    border: isActive ? "1px solid var(--brand)" : "1px solid var(--line)",
    borderRadius: "999px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "36px",
  });

  const navBtnStyle = (disabled) => ({
    padding: "6px 14px",
    fontSize: "13px",
    fontWeight: 500,
    color: disabled ? "var(--line-strong)" : "var(--ink-2)",
    backgroundColor: "transparent",
    border: "1px solid var(--line)",
    borderRadius: "999px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <p className="page-title mb-0" style={{ margin: 0 }}>
          Categories List
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "nowrap" }}>
          <div style={{ ...searchContainerStyles, minWidth: "200px" }}>
            <div style={searchIconStyles}>
              <SearchIcon color={isFocused ? "var(--brand)" : "var(--ink-3)"} />
            </div>
            <input
              id="categories-search-input"
              type="text"
              placeholder="Search by category name…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              style={isFocused ? searchInputFocusStyles : searchInputStyles}
            />
            {search && (
              <button
                onClick={() => onSearchChange("")}
                style={clearBtnStyles}
                title="Clear search"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--brand)";
                  e.currentTarget.style.backgroundColor = "var(--brand-tint)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "var(--ink-3)";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <ClearIcon />
              </button>
            )}
          </div>
          <button className="btn fribe-primary-btn" onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      {categories.length ? (
        <>
          <table className="table">
            <thead>
              <tr>
                <th className="user-header-item">Name</th>
                <th className="user-header-item header-item-center">Status</th>
                <th className="user-header-item">Icon</th>
                <th className="user-header-item header-item-center">Color</th>
                <th className="user-header-item header-item-center">Created At</th>
                <th className="user-header-item header-item-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((item) => {
                const { shortId: id, name, createdAt, icon, color: initialColor } = item ?? {};
                return (
                  <tr className="data-row-verticle-middle" key={id}>
                    <td className="user-data-item data-item-captilize">
                      <HighlightText text={name} highlight={search} />
                    </td>
                    <td className="user-data-item data-item-center">
                      <Switch
                        checked={item.status}
                        onChange={(e) => handleSwitchChange(e, id)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </td>
                    <td className="user-data-item">
                      <ImageWithFallback icon={icon} />
                    </td>
                    <td className="user-data-item data-item-center" style={{ position: 'relative' }}>
                      <button className="btn" onClick={() => toggleColorPicker(id)}>
                        <MdColorLens />
                      </button>
                      <span
                        style={{
                          padding: '5px 5px',
                          color: 'black',
                          borderRadius: '5px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          display: 'inline-block',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        {selectedCategory === id ? color : initialColor || '#ffffff'}
                      </span>
                      {showColorPicker === id && (
                        <div ref={colorPickerRef} style={{ position: 'absolute', zIndex: 2 }}>
                          <SketchPicker
                            color={selectedCategory === id ? color : initialColor || '#ffffff'}
                            onChange={(color) => handleColorChange(color, id)}
                          />
                        </div>
                      )}
                    </td>
                    <td className="user-data-item data-item-center">
                      {moment(createdAt).format("MMM D, YYYY [at] HH:mm A")}
                    </td>
                    <td className="user-data-item data-item-center">
                      <button
                        className="btn"
                        onClick={() => {
                          onEdit(id, item);
                        }}
                      >
                        <MdEdit />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Pagination ── */}
          {pagination.total > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
                marginTop: "16px",
                padding: "12px 0",
              }}
            >
              <span style={{ fontSize: "13px", color: "var(--ink-3)" }}>
                Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} categories
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page === 1}
                  style={navBtnStyle(pagination.page === 1)}
                >
                  Previous
                </button>
                {getPageNumbers().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} style={{ padding: "0 4px", color: "var(--ink-3)" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => onPageChange(p)}
                      style={paginationBtnStyle(p === pagination.page)}
                      onMouseEnter={(e) => {
                        if (p !== pagination.page) {
                          e.currentTarget.style.backgroundColor = "var(--land)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (p !== pagination.page) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => onPageChange(Math.min(pagination.totalPages, pagination.page + 1))}
                  disabled={pagination.page === pagination.totalPages}
                  style={navBtnStyle(pagination.page === pagination.totalPages)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div>No matching result found</div>
      )}
    </div>
  );
};

export default CategoriesListTable;
